/**
 * Two Column: esconder a 2ª coluna quando o grupo ativo tem 1 link só.
 * Por quê: no dashboard (e folhas SA) a 2ª coluna só mostra "Painel" — ruído.
 * Classe Velzon `twocolumn-panel` no body = só ícones (DOC 09 § 7.3).
 * LAC-190: preferência “sempre ícones” força collapse até o clique em grupo 2+.
 */

import { readAlwaysIconsOnly, type TwoColumnShellScope } from "../helpers/twoColumnAlwaysIcons";

type NavSubItem = { link?: string; childItems?: NavSubItem[] };
type NavItem = {
  id?: string;
  subItems?: NavSubItem[];
};

function normalizePath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

function linkMatches(path: string, link: string): boolean {
  const target = normalizePath(link);
  if (!target || target === "/#") return false;
  return path === target || path.startsWith(`${target}/`);
}

/** Inferir shell pela rota — mesmo critério do resolveShellNavdata. */
export function shellScopeFromPath(pathname: string): TwoColumnShellScope {
  return normalizePath(pathname).startsWith("/super-admin") ? "sa" : "tenant";
}

/** Grupo cujo subitem (ou filho) melhor casa com a rota atual. */
export function findNavGroupForPath(
  navData: NavItem[] | null | undefined,
  pathname: string
): NavItem | null {
  const path = normalizePath(pathname);
  let best: { group: NavItem; linkLen: number } | null = null;

  for (const group of navData || []) {
    for (const sub of group.subItems || []) {
      const candidates = [
        sub.link,
        ...((sub.childItems || []).map((c) => c.link) || []),
      ].filter(Boolean) as string[];

      for (const link of candidates) {
        if (!linkMatches(path, link)) continue;
        const len = normalizePath(link).length;
        if (!best || len > best.linkLen) {
          best = { group, linkLen: len };
        }
      }
    }
  }

  return best?.group ?? null;
}

/**
 * true = só ícones (2ª coluna oculta).
 * Com always-icons: sempre true na sync de rota; clique em grupo 2+ abre via openTwoColumnSubmenu.
 */
export function shouldCollapseTwoColumnPanel(
  navData: NavItem[] | null | undefined,
  pathname: string,
  alwaysIcons?: boolean
): boolean {
  const preferAlways =
    alwaysIcons ?? readAlwaysIconsOnly(shellScopeFromPath(pathname));
  if (preferAlways) {
    return true;
  }
  const group = findNavGroupForPath(navData, pathname);
  if (!group) return true;
  return (group.subItems?.length ?? 0) <= 1;
}

export function syncTwoColumnPanelForPath(
  navData: NavItem[] | null | undefined,
  pathname: string
): void {
  const collapse = shouldCollapseTwoColumnPanel(navData, pathname);
  document.body.classList.toggle("twocolumn-panel", collapse);
}

/** Ao abrir grupo com 2+ links, mostrar a 2ª coluna (mesmo com always-icons). */
export function openTwoColumnSubmenu(): void {
  document.body.classList.remove("twocolumn-panel");
}
